import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { HOOK__get_div_size } from '@/pages/page_generation/_hooks/HOOK__get_div_size';
import { G_CONTEXT_MENU__field } from '@/pages/page_generation/sidebar/_base/context_menu/G_CONTEXT_MENU__field';
import { path_relative_to_absolute } from '@/utils/utils_files_client';
import { not_implemented_type } from '@shared/asserts/utils_asserts';
import { DTO__image_size } from '@shared/dto/+image/image_asset_base/DTO__image_size';
import { ENUM__image_transform, ENUM_UTILS__image_transform } from '@shared/dto/generation/graph_invoke/_fields/image/_enums/ENUM__image_transform';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';
import { useMemo, useState } from 'react';
import { HOOK__image_drag } from '../../../../../../_hooks/HOOK__image_drag';
import { G_GRAPH__IMAGE__labels } from '../_base/G_GRAPH__IMAGE__labels';
import { G_IMAGE_OVERLAY__crop_box } from '../_overlays/G_IMAGE_OVERLAY__crop_box';
import { G_IMAGE_OVERLAY__mask } from '../_overlays/G_IMAGE_OVERLAY__mask';
import { G_IMAGE_OVERLAY__type_mismatch } from '../_overlays/G_IMAGE_OVERLAY__type_mismatch';
import { G_IMAGE__view_loading } from './G_IMAGE__view_loading';
import { G_IMAGE__view_no_image } from './G_IMAGE__view_no_image';
import { G_IMAGE__view_upload } from './G_IMAGE__view_upload';

interface PROPS {
  field: FIELD__image_asset;
  draggable?: boolean;
  children?: React.ReactNode;
  class_name_container?: string;
  class_name_image?: string;
}

export function G_IMAGE__master_component({ field, children = null, draggable = true, class_name_container = 'size-full', class_name_image = '' }: PROPS) {
  const dispatch = use_dispatch();

  let { preprocessing_transform__current } = field;
  /*
   * STORE
   */
  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_general__ui_disabled } = use_slice_ui(['g_general__ui_disabled']);

  /*
   * STATE
   */
  let { graph } = generation_current.tool;

  let image_main_category = field.category;

  const [is_hovered, set_is_hovered] = useState(false);

  const { ref_element__set, size: container_size } = HOOK__get_div_size();

  const size__image_original = useMemo(() => get_image_size(), [field]);
  const size__cell_scaled = useMemo(() => get_cell_size_scaled(), [size__image_original, container_size, graph, field]);

  let can_be_changed_manually = useMemo(() => get_can_be_changed_manually(), [field, graph]);

  const { onDragStart, onDragEnd } = HOOK__image_drag(field.asset);

  /*
   * EVENTS
   */

  /*
   * METHODS
   */

  function get_cell_ar() {
    if (size__image_original.is_zero()) return 1;

    let { graph } = generation_current.tool;

    let cell_ar = graph.invocation_grid2d_view.grid2d_cell__aspect_ratio;

    let out;

    switch (preprocessing_transform__current) {
      case ENUM__image_transform.NONE:
        out = size__image_original.aspect_ratio;
        break;
      case ENUM__image_transform.CONTAIN:
      case ENUM__image_transform.COVER:
      case ENUM__image_transform.FILL:
        out = cell_ar;
        break;
      default:
        not_implemented_type(preprocessing_transform__current);
    }

    return out;
  }

  function get_image_size() {
    if (!field.get_has_content()) return DTO__image_size.ZERO.clone();

    return field.get_image_size().clone();
  }

  function get_cell_size_scaled() {
    if (size__image_original.is_zero()) return DTO__image_size.ZERO.clone();
    if (container_size.is_zero()) return DTO__image_size.ZERO.clone();

    const cell_ar = get_cell_ar();

    // NONE keeps the original image aspect ratio.
    // All preprocessing modes use the grid cell aspect ratio.
    const width_from_height = container_size.height * cell_ar;
    const height_from_width = container_size.width / cell_ar;

    let width: number;
    let height: number;

    if (width_from_height <= container_size.width) {
      width = width_from_height;
      height = container_size.height;
    } else {
      width = container_size.width;
      height = height_from_width;
    }

    return new DTO__image_size().clone({
      width,
      height,
    });
  }

  function get_can_be_changed_manually() {
    if (field.meta.graph_id != graph.meta.graph_id) return false;

    return field.is_ready_for_manual_edit();
  }

  // Handle background color requirement for CONTAIN transform
  function get_transform_background_style(transform?: ENUM__image_transform) {
    if (transform === ENUM__image_transform.CONTAIN) {
      return { backgroundColor: 'black' };
    }
    return {};
  }

  /*
   * RENDERS
   */

  function render_image() {
    let { asset: image_asset } = field;

    const url_absolute = path_relative_to_absolute(image_asset.url_relative_latest);
    const fit_class = ENUM_UTILS__image_transform.to_css(preprocessing_transform__current);
    const bg_style = get_transform_background_style(preprocessing_transform__current);

    return (
      <G_CONTEXT_MENU__field {...{ field }}>
        <img
          src={url_absolute}
          alt={`${image_asset.image_size.width}x${image_asset.image_size.height}`}
          role="button"
          {...{ onDragStart, onDragEnd, draggable }}
          style={bg_style}
          className={`nodrag size-full max-w-full max-h-full ${fit_class} rounded
          border border-2 border-component-border
          ${draggable && 'cursor-grab'}
          ${class_name_image}
          `}
        />
      </G_CONTEXT_MENU__field>
    );
  }

  function render_drag_border() {
    if (!draggable) return null;
    if (!is_hovered) return null;
    if (g_general__ui_disabled) return null;

    return (
      <div
        className={`absolute inset-0 size-full 
            border border-4  border-component-border 
            rounded pointer-events-none
        `}
      />
    );
  }

  /*
   * HTML
   */

  if (field.meta.data__is_linked) return <G_IMAGE__view_no_image {...{ field }} text={'Data is Linked'} />;

  //no content yet
  if (!field.get_has_content() && field.meta.kind__is_output) return <G_IMAGE__view_no_image {...{ field }} text={'Not generated yet'} />;

  //not an usuall image
  if (!field.get_has_content() && !field.is_category_image) return <G_IMAGE__view_no_image text={`PUT ${image_main_category} IMAGE HERE`} />;

  //no content and can upload
  if (!field.get_has_content() && can_be_changed_manually) return <G_IMAGE__view_upload {...{ field }} />;

  //no content and cant upload
  if (!field.get_has_content() && !can_be_changed_manually) return <G_IMAGE__view_no_image text={'No image'} />;

  if (!field.image__is_initialized) return <G_IMAGE__view_loading {...{ hash_sha256_content: field.hash_sha256_content }} />;

  return (
    <>
      <div ref={ref_element__set} className={`info__image_master_component nodrag relative  flex-center ${class_name_container}`}>
        <div
          className="info__image_master_inner_content relative"
          style={size__cell_scaled.to_css_object()}
          onMouseEnter={() => set_is_hovered(true)}
          onMouseLeave={() => set_is_hovered(false)}
        >
          {render_image()}

          <G_IMAGE_OVERLAY__crop_box field={field} {...{ container_size }} />

          {/* <G_IMAGE_OVERLAY__mask_animated field={field} is_animated={true} /> */}
          <G_IMAGE_OVERLAY__mask field={field} />

          {!is_hovered && <G_IMAGE_OVERLAY__type_mismatch field={field} />}

          {render_drag_border()}

          {children}

          <G_GRAPH__IMAGE__labels {...{ field }} />
        </div>
      </div>
    </>
  );
}
